import { useEffect, useRef, useState } from 'react'

import type { JourneyCoreState, JourneySpec } from './types'

/**
 * Drives a looping journey from a timeline.
 *
 * Nothing runs unless `active` is true, and every timer is cleared on
 * deactivate/unmount, so a closed dialog —or an archived announcement nobody is
 * looking at— costs nothing. That matters more than it used to: the dialog now
 * holds several announcements and only one of them is ever on screen.
 *
 * `clickSeq` deliberately survives the reset between cycles. It keys the ripple
 * element, and reusing a key React has already seen would skip the animation.
 */
export const useJourney = <S extends JourneyCoreState>(spec: JourneySpec<S>, active: boolean): S => {
  const [state, setState] = useState<S>(spec.initial)
  const timers = useRef<number[]>([])

  // The spec is rebuilt on every render by the announcement that owns it, so it
  // cannot be an effect dependency without restarting the loop continuously.
  const specRef = useRef(spec)
  specRef.current = spec

  useEffect(() => {
    const clearTimers = () => {
      timers.current.forEach((id) => window.clearTimeout(id))
      timers.current = []
    }

    if (!active) {
      clearTimers()
      setState(specRef.current.initial)

      return
    }

    const { initial, loopMs, build } = specRef.current
    const timeline = build()

    const runCycle = () => {
      clearTimers()
      setState((prev) => ({ ...initial, clickSeq: prev.clickSeq }))

      timeline.forEach((step) => {
        timers.current.push(
          window.setTimeout(() => {
            setState((prev) => ({
              ...prev,
              ...step.patch,
              clickSeq: step.click ? prev.clickSeq + 1 : prev.clickSeq,
              clicking: step.click ? true : prev.clicking,
            }))

            if (step.click) {
              timers.current.push(window.setTimeout(() => setState((prev) => ({ ...prev, clicking: false })), 150))
            }
          }, step.at)
        )
      })

      timers.current.push(window.setTimeout(runCycle, loopMs))
    }

    runCycle()

    return clearTimers
  }, [active])

  return state
}
